import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { useGetUsersByGroupId } from 'src/hooks/queries/user-group/useGetUsersByGroupId';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import type { CollectionData } from '@/utils/collectionUtils';
import { useGetTableProps } from '@/utils/table/hooks/useGetTableProps';
import type { TableFields, TablePropsWithActions } from '@/utils/table/types';
import { dateColumnFromConfig } from '@/utils/table/utils/dateColumn';

type GetUsersByGroupIdData = NonNullable<
  ReturnType<typeof useGetUsersByGroupId>['data']
>;

type UserGroupMembersTableFields = CollectionData<
  GetUsersByGroupIdData['user_group'][0]['users'][0]
>;

export type GroupMembersTableFields =
  UserGroupMembersTableFields['authUsers'] & {
    Name: null | string;
    CreatedByUserName: null | string;
    CreatedAtTimestamp: string;
    /**
     * COI-714: a MAPPED field now, like `Name` above — no longer a
     * passthrough of the query's top-level `Status`. `auth.user`'s
     * tenant-global column is deprecated, so this is flattened from the
     * caller org's `organisationusers` membership row in `labelledFields`.
     */
    Status: string | null | undefined;
  };

const getNameForUser = (user?: UserGroupMembersTableFields['authUsers']) => {
  if (!user) {
    return '-';
  }

  return `${user.FirstName || ''} ${user.LastName || ''}`.trim() || '-';
};

export const useGetCollectionTableProps = (
  records: UserGroupMembersTableFields[]
): TablePropsWithActions<GroupMembersTableFields> => {
  const { t } = useTranslation(['common'], { keyPrefix: 'userGroupMembers' });
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'userGroupMembers.columns',
  });
  const isTrpcEnabled = useIsFeatureFlagEnabled('trpc');

  const labelledFields = useMemo<GroupMembersTableFields[]>(
    () =>
      records
        ?.filter((u) => u.authUsers)
        .map((record) => {
          return {
            ...record.authUsers,
            Name: getNameForUser(record?.authUsers),
            CreatedByUserName: record.createdByUser?.FriendlyName || '-',
            CreatedAtTimestamp: record?.CreatedAtTimestamp,
            // COI-714: org-scoped only. The `?? record.authUsers?.Status`
            // fallback reached for the tenant-global `auth.user."Status"`,
            // which is last-writer-wins across orgs and is being
            // decommissioned. The nested array is the caller org's single
            // membership row.
            Status: record.authUsers?.organisationusers?.[0]?.Status,
          };
        }),
    [records]
  );

  const fields: TableFields<GroupMembersTableFields> = {
    Name: {
      id: 'name',
      header: st('name'),
    },
    FirstName: {
      header: st('firstName'),
    },
    LastName: {
      header: st('lastName'),
    },
    FriendlyName: {
      header: st('username'),
    },
    Email: {
      header: st('email'),
    },
    ...(!isTrpcEnabled
      ? {
          RoleKey: {
            header: st('role'),
          },
        }
      : {}),
    Status: {
      header: st('status'),
    },
    CreatedByUserName: {
      header: st('created_by_user'),
    },
    CreatedAtTimestamp: dateColumnFromConfig({
      header: { header: st('created_on') },
      dateField: 'CreatedAtTimestamp',
    }),
  };

  return useGetTableProps({
    data: labelledFields,
    entityLabel: t('entity_name'),
    fields,
    initialColumns: ['FriendlyName', 'Email', 'CreatedAtTimestamp'],
    preferencesStorageKey: 'GroupMembersSettingsTable-Preferences',
    customAttributeFormIds: [],
  });
};
